import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { loadVGSCollect } from '@vgs/collect-js';
import PropTypes from 'prop-types';
import getAcceptedPaymentCards from '../../../../helpers/getAcceptedPaymentCards';
import { getVGSVault, getVGSVaultRouteId, isTestEnvironment } from '../../../../helpers/getVGSProxyEndpoint';
import { changeValidation } from '../../../../store/slices/trip';
import { CreditCardsFormValidation, DO_USE_VGS_INPUT_FORM } from '../../../../constants';
import { VGS_FORM_DEFAULTS } from '../../../../constants/VgsFormConstants';
import {
  evaluateVgsFormState,
} from '../../../../helpers/evaluateVgsFormState';
import getCardCodeFromVGSCardType from '../../../../helpers/getCardCodeFromVGSCardType';

const { DO_SHOW_SECURITY_CODE_INPUT, DO_TAKE_CARD_ADDRESS_INPUTS } = VGS_FORM_DEFAULTS;

// Internal context for managing multiple form instances
const VgsFormInstancesContext = createContext(null);

// Provider that manages multiple VGS form instances (one per cardIndex)
const VgsFormInstancesProvider = ({ children }) => {
  const dispatch = useDispatch();
  const creditCards = useSelector((store) => store.trip.creditCards);
  const availableStates = useSelector((store) => store.user.availableStates);
  const chargeableItemOptions = useSelector((store) => store.trip.chargeableItemOptions);
  const tripId = useSelector((store) => {
    const { selectedTrips } = store.trip;
    return selectedTrips ? Object.keys(selectedTrips)[0] : null;
  });
  const trip = useSelector((store) => (tripId ? store.trip.selectedTrips[tripId] : null));
  const hotelItineraries = trip?.hotelItineraries;
  const hotelItinerariesCount = trip && hotelItineraries
    && (Object.keys(hotelItineraries).length > 0)
    && Object.keys(hotelItineraries).length;
  const acceptedPaymentCards = useMemo(() => (hotelItinerariesCount
    ? getAcceptedPaymentCards(Array.isArray(hotelItineraries)
      ? hotelItineraries : Object.values(hotelItineraries)) : {}),
  [hotelItinerariesCount, hotelItineraries]);

  // Map of cardIndex to form instances and their state
  const [formInstances, setFormInstances] = useState({});
  const [errorsMap, setErrorsMap] = useState({});
  const [tokenizedDataMap, setTokenizedDataMap] = useState({});
  const [isFormValidMap, setIsFormValidMap] = useState({});
  const [isFormFocusedMap, setIsFormFocusedMap] = useState({});
  const [isFormActiveMap, setIsFormActiveMap] = useState({});
  const [generalErrorMap, setGeneralErrorMap] = useState({});
  const creditCardRefs = useRef({});
  const availableStatesRef = useRef(availableStates);

  useEffect(() => {
    availableStatesRef.current = availableStates;
  }, [availableStates]);

  // Update credit card refs when creditCards change
  useEffect(() => {
    if (!creditCards) {
      creditCardRefs.current = {};
      return;
    }
    const nextRefs = {};
    Object.keys(creditCards).forEach((index) => {
      nextRefs[index] = creditCards[index];
    });
    creditCardRefs.current = nextRefs;
  }, [creditCards]);

  const evaluateFormState = useCallback((cardIndexStr, state) => {
    if (!state) return;

    const result = evaluateVgsFormState(state, {
      doShowSecurityCodeInput: DO_SHOW_SECURITY_CODE_INPUT,
      doTakeCardAddressInputs: DO_TAKE_CARD_ADDRESS_INPUTS,
      acceptedPaymentCards,
      currentCreditCard: creditCardRefs.current[cardIndexStr],
      availableStates: availableStatesRef.current,
    });

    setGeneralErrorMap((prev) => ({ ...prev, [cardIndexStr]: result.generalError }));
    setErrorsMap((prev) => ({ ...prev, [cardIndexStr]: result.errors }));

    // Check if any field is currently focused
    const {
      cardNumber: card, expiration: exp, cardHolderName: name, cvc,
    } = state;
    const hasFocusedField = (card && card.isFocused)
      || (exp && exp.isFocused)
      || (name && name.isFocused)
      || (DO_SHOW_SECURITY_CODE_INPUT && cvc && cvc.isFocused)
      || (DO_TAKE_CARD_ADDRESS_INPUTS);
    setIsFormFocusedMap((prev) => ({ ...prev, [cardIndexStr]: hasFocusedField }));

    // Check if user is actively inputting
    const hasActiveField = hasFocusedField
      || (card && card.isDirty)
      || (exp && exp.isDirty)
      || (name && name.isDirty)
      || (DO_SHOW_SECURITY_CODE_INPUT && cvc && cvc.isDirty);
    setIsFormActiveMap((prev) => ({ ...prev, [cardIndexStr]: hasActiveField }));

    // Check if form is valid for tokenization
    const isCardValid = card && card.isValid && !result.errors.cardNumber;
    const isExpValid = exp && exp.isValid;
    const isNameValid = name && name.isValid;
    const isCvcValid = (DO_SHOW_SECURITY_CODE_INPUT && state.cvc)
      ? (cvc && cvc.isValid) : true;

    // Calculate address validity
    const currentCreditCard = creditCardRefs.current[cardIndexStr];
    const shouldValidateAddress = DO_TAKE_CARD_ADDRESS_INPUTS
      && currentCreditCard
      && state.address;
    let isAddressValid = true;
    if (shouldValidateAddress) {
      const hasAddressLine1 = currentCreditCard.addressLineOne
        && currentCreditCard.addressLineOne.trim();
      const hasCity = currentCreditCard.city && currentCreditCard.city.trim();
      const hasZip = currentCreditCard.billingCode && currentCreditCard.billingCode.trim();
      const hasCountry = currentCreditCard.country && currentCreditCard.country.trim();
      const currentAvailableStates = availableStatesRef.current;
      const needsState = currentCreditCard.country
        && currentAvailableStates
        && currentAvailableStates.length > 0;
      const hasState = !needsState
        || (currentCreditCard.state && currentCreditCard.state.trim());
      isAddressValid = hasAddressLine1 && hasCity && hasZip && hasCountry && hasState;
    }
    const formIsValid = isCardValid && isExpValid && isNameValid
      && isCvcValid && isAddressValid;
    setIsFormValidMap((prev) => ({ ...prev, [cardIndexStr]: formIsValid }));
  }, [acceptedPaymentCards]);

  useEffect(() => {
    if (!creditCards) return;
    Object.entries(formInstances || {}).forEach(([cardIndexStr, form]) => {
      if (form && form.state) {
        evaluateFormState(cardIndexStr, form.state);
      }
    });
  }, [creditCards, availableStates, formInstances, evaluateFormState]);

  // Function to initialize a form instance for a specific cardIndex
  const initializeFormInstance = useCallback(async (cardIndex) => {
    const cardIndexStr = String(cardIndex);

    // If form already exists, don't reinitialize
    if (formInstances[cardIndexStr] && formInstances[cardIndexStr].field) {
      return formInstances[cardIndexStr];
    }

    const vgsCollect = await loadVGSCollect({
      vaultId: getVGSVault(),
      environment: isTestEnvironment() ? 'sandbox' : 'live',
      version: '3.2.2',
    }).catch(() => null);

    if (!vgsCollect) return null;

    const vgsFormLocal = vgsCollect.init((state) => {
      evaluateFormState(cardIndexStr, state);
    }).setRouteId(getVGSVaultRouteId());

    setFormInstances((prev) => ({ ...prev, [cardIndexStr]: vgsFormLocal }));
    return vgsFormLocal;
  }, [formInstances, evaluateFormState]);

  // Function to tokenize for a specific cardIndex
  const tokenizeForCard = useCallback(async (cardIndex) => {
    const cardIndexStr = String(cardIndex);
    const form = formInstances[cardIndexStr];
    const createTokenizationError = (message) => {
      const error = new Error(message || 'Unable to process the payment information.');
      error.name = 'PaymentProcessingError';
      return error;
    };

    if (!form || !form.field || !form.submit || typeof form.submit !== 'function') {
      throw createTokenizationError('Payment is not ready for processing.');
    }

    const currentState = form.state;
    if (!currentState) {
      throw createTokenizationError('Payment state is unavailable.');
    }

    try {
      const cardState = currentState.cardNumber;
      let cardNumberLast4Chars = null;
      let cardType = null;
      let cardCode = null;

      if (cardState) {
        if (cardState.last4) {
          cardNumberLast4Chars = cardState.last4;
        } else if (cardState.value) {
          const digitsOnly = cardState.value.replace(/\D/g, '');
          if (digitsOnly.length >= 4) {
            cardNumberLast4Chars = digitsOnly.slice(-4);
          }
        }
        if (cardState.cardType) {
          cardType = cardState.cardType;
          cardCode = getCardCodeFromVGSCardType(cardType);
        }
      }

      const data = await new Promise((resolve, reject) => {
        try {
          form.submit('/post', {}, (status, tokenized) => {
            if (status === 200 && tokenized) {
              const enhancedData = {
                ...tokenized,
                cardNumberLast4Chars,
                cardCode,
              };
              const currentCreditCard = creditCardRefs.current[cardIndexStr];
              if (DO_TAKE_CARD_ADDRESS_INPUTS && currentCreditCard) {
                enhancedData.address_line1 = currentCreditCard.addressLineOne || '';
                enhancedData.address_line2 = currentCreditCard.addressLineTwo || '';
                enhancedData.address_city = currentCreditCard.city || '';
                enhancedData.address_state = currentCreditCard.state || '';
                enhancedData.address_zip = currentCreditCard.billingCode || '';
                enhancedData.address_country = currentCreditCard.country || '';
              }
              resolve(enhancedData);
              return;
            }
            reject(createTokenizationError(`Tokenize submit failed with status ${status}`));
          });
        } catch (error) {
          reject(error);
        }
      });

      if (!data) {
        throw createTokenizationError('Tokenization returned no data.');
      }

      setTokenizedDataMap((prev) => ({ ...prev, [cardIndexStr]: data }));
      setGeneralErrorMap((prev) => ({ ...prev, [cardIndexStr]: '' }));
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Tokenize: Exception', error);
      setTokenizedDataMap((prev) => ({ ...prev, [cardIndexStr]: null }));
      setGeneralErrorMap((prev) => ({ ...prev, [cardIndexStr]: 'Unable to tokenize payment information. Please verify your card details and try again.' }));
      throw (error instanceof Error ? error : createTokenizationError());
    }
  }, [formInstances]);

  const getActiveCardEntries = useCallback(() => {
    const cards = creditCardRefs.current || {};
    const entries = Object.entries(cards);
    if (!entries.length) return [];

    const cardsWithSelections = entries
      .filter(([, card]) => Array.isArray(card?.selectedFor) && card.selectedFor.length > 0);
    if (cardsWithSelections.length) {
      return cardsWithSelections;
    }

    if (entries.length > 1) {
      return entries;
    }

    const picked = entries.filter(([, card]) => card && card.isPicked);
    return picked.length ? picked : entries.slice(0, 1);
  }, []);

  const tokenizeSelectedCards = useCallback(async () => {
    const activeCards = getActiveCardEntries();
    if (!activeCards.length) {
      return null;
    }

    const invalidFormIndices = activeCards
      .filter(([index]) => !isFormValidMap[index])
      .map(([index]) => index);

    if (invalidFormIndices.length) {
      const errorMessage = 'Please complete all required credit card fields.';
      setGeneralErrorMap((prev) => ({
        ...prev,
        ...invalidFormIndices.reduce((acc, idx) => ({
          ...acc,
          [idx]: errorMessage,
        }), {}),
      }));
      throw new Error(errorMessage);
    }

    const totalCards = Object.keys(creditCardRefs.current || {}).length;
    const requiresChargeSelection = totalCards > 1
      && Array.isArray(chargeableItemOptions)
      && chargeableItemOptions.length > 0;
    if (requiresChargeSelection) {
      const allEntries = Object.entries(creditCardRefs.current || {});
      const selectionCandidates = allEntries.length ? allEntries : activeCards;
      const cardsMissingSelection = selectionCandidates
        .filter(([, card]) => !Array.isArray(card?.selectedFor) || !card.selectedFor.length);

      if (cardsMissingSelection.length) {
        cardsMissingSelection.forEach(([index, card]) => {
          dispatch(changeValidation({
            field: 'creditCards',
            targetIndex: Number(index),
            formValidations: {
              ...CreditCardsFormValidation,
              ...(card?.formValidations || {}),
              selectedFor: 'Please select at least one item for this card.',
            },
          }));
        });
        throw new Error('Assign at least one chargeable item to each card.');
      }
    }

    const results = {};
    for (let i = 0; i < activeCards.length; i += 1) {
      const [index] = activeCards[i];
      const numericIndex = Number(index);
      if (!formInstances[index] || !formInstances[index].field) {
        // eslint-disable-next-line no-await-in-loop
        await initializeFormInstance(numericIndex);
      }
      // eslint-disable-next-line no-await-in-loop
      results[index] = await tokenizeForCard(numericIndex);
    }
    return results;
  }, [
    chargeableItemOptions,
    dispatch,
    getActiveCardEntries,
    initializeFormInstance,
    isFormValidMap,
    formInstances,
    tokenizeForCard,
  ]);

  const contextValue = useMemo(() => ({
    initializeFormInstance,
    tokenizeForCard,
    getActiveCardEntries,
    tokenizeSelectedCards,
    formInstances,
    errorsMap,
    tokenizedDataMap,
    isFormValidMap,
    isFormFocusedMap,
    isFormActiveMap,
    generalErrorMap,
  }), [
    initializeFormInstance,
    tokenizeForCard,
    getActiveCardEntries,
    tokenizeSelectedCards,
    formInstances,
    errorsMap,
    tokenizedDataMap,
    isFormValidMap,
    isFormFocusedMap,
    isFormActiveMap,
    generalErrorMap,
  ]);

  return (
    <VgsFormInstancesContext.Provider value={contextValue}>
      {children}
    </VgsFormInstancesContext.Provider>
  );
};

// Hook to get payment context for a specific cardIndex
// If cardIndex is undefined, defaults to 0 for backward compatibility
export const usePayment = (cardIndex) => {
  const instancesContext = useContext(VgsFormInstancesContext);
  const creditCards = useSelector((store) => store.trip.creditCards);
  const chargeableItemOptions = useSelector((store) => store.trip.chargeableItemOptions);
  const totalCardCount = Object.keys(creditCards || {}).length;
  const requiresSelection = totalCardCount > 1
    && Array.isArray(chargeableItemOptions)
    && chargeableItemOptions.length > 0;
  const isCardSpecific = cardIndex !== undefined && cardIndex !== null;
  const effectiveCardIndex = isCardSpecific ? Number(cardIndex) : 0;
  const cardIndexKey = String(effectiveCardIndex);

  useEffect(() => {
    if (!instancesContext) return;
    if (!instancesContext.formInstances[cardIndexKey]) {
      instancesContext.initializeFormInstance(effectiveCardIndex);
    }
  }, [instancesContext, effectiveCardIndex, cardIndexKey]);

  const {
    formInstances = {},
    errorsMap = {},
    tokenizedDataMap = {},
    isFormValidMap = {},
    isFormFocusedMap = {},
    isFormActiveMap = {},
    generalErrorMap = {},
    tokenizeForCard,
    tokenizeSelectedCards,
    getActiveCardEntries,
  } = instancesContext || {};

  const tokenizeFunc = useCallback(async () => {
    if (!instancesContext) {
      throw new Error('Payment context unavailable');
    }
    if (!isCardSpecific) {
      if (!tokenizeSelectedCards) {
        throw new Error('Tokenization not available');
      }
      return tokenizeSelectedCards();
    }
    if (!tokenizeForCard) {
      throw new Error('Tokenization not available');
    }
    return tokenizeForCard(effectiveCardIndex);
  }, [instancesContext, isCardSpecific, tokenizeSelectedCards, tokenizeForCard,
    effectiveCardIndex]);

  if (!instancesContext) {
    return {
      form: null,
      errors: {},
      tokenizedData: null,
      tokenizeFunc,
      isFormValid: false,
      isFormFocused: false,
      isFormActive: false,
      generalError: '',
    };
  }

  if (!isCardSpecific) {
    const activeEntries = getActiveCardEntries
      ? getActiveCardEntries()
      : Object.entries(creditCards || {});
    let relevantEntries = activeEntries.length ? activeEntries : Object.entries(creditCards || {});
    if (requiresSelection) {
      relevantEntries = Object.entries(creditCards || {});
    }
    const areFormsValid = !relevantEntries.length
      || relevantEntries.every(([index]) => isFormValidMap[index]);
    const hasSelectionError = requiresSelection
      ? relevantEntries.some(([, card]) => !Array.isArray(card?.selectedFor)
      || !card.selectedFor.length)
      : false;
    return {
      tokenizeFunc,
      isFormValid: areFormsValid && !hasSelectionError,
    };
  }

  const form = formInstances[cardIndexKey] || null;
  const errors = errorsMap[cardIndexKey] || {
    cardNumber: '',
    expiration: '',
    cardHolderName: '',
    cvc: '',
    address_line1: '',
    address_line2: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    address_country: '',
  };
  const tokenizedData = tokenizedDataMap[cardIndexKey] || null;
  const isFormValid = isFormValidMap[cardIndexKey] || false;
  const isFormFocused = isFormFocusedMap[cardIndexKey] || false;
  const isFormActive = isFormActiveMap[cardIndexKey] || false;
  const generalError = generalErrorMap[cardIndexKey] || '';

  return {
    form,
    errors,
    tokenizedData,
    tokenizeFunc,
    isFormValid,
    isFormFocused,
    isFormActive,
    generalError,
  };
};

// Main provider component (wrapper for backward compatibility)
export const VgsIFrameInputProvider = ({ children }) => (DO_USE_VGS_INPUT_FORM ? (
  <VgsFormInstancesProvider>
    {children}
  </VgsFormInstancesProvider>
) : children);

VgsFormInstancesProvider.propTypes = {
  children: PropTypes.node,
};

VgsFormInstancesProvider.defaultProps = {
  children: null,
};

VgsIFrameInputProvider.propTypes = {
  children: PropTypes.node,
};

VgsIFrameInputProvider.defaultProps = {
  children: null,
};
